import { useContext, useState } from 'react';
import { Grid } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTable, SttFormSelect, SttButton } from '../all';
import useGroupsService from '../../services/GroupsService';
import { FAMILY_BENEFICIARIES, GROUP_MEMBERS } from '../../lib/queryKeys';
import { SttSelectUser } from '../selectors/SttSelectUser';
import DateBuilder from '../../lib/DateBuilder';
import { getDateLocale } from '../../lib/dateLocale';
import { getErrorMessage, safeFormatDate } from '../../lib/utils';

type Member = {
    id: number;
};

type User = {
    id: number;
    name: string;
    surname1?: string;
    surname2?: string;
};

type Beneficiary = {
    id: number;
    name: string;
    created: string;
    holderRelation: string;
};

type BeneficiariesResponse = {
    rows: Beneficiary[];
    count: number;
};

type Props = {
    member: Member;
};

/**
 *    Beneficiaries lists
 *    ============
 *  List of all the beneficiaries
 *    Props:
 *  member owner of the abono
 *
 */

export default function SttBeneficiariesList({
    member,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const groupsService = useGroupsService();
    const [user, setUser] = useState<User | null>(null);
    const [relation, setRelation] = useState<string>('OFFSPRING');

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const relationLabels: Record<string, string> = {
        OFFSPRING: t('offspring'),
        PARENT: t('partner'),
        PARTNER: t('partner'),
        OTHER: t('groups.beneficiaries.other'),
    };

    const beneficiariesQuery = useQuery({
        queryKey: [
            FAMILY_BENEFICIARIES,
            member.id,
            { page, rows: rowsPerPage },
        ],
        queryFn: () =>
            groupsService.getBeneficiaries(member.id, {
                page,
                rows: rowsPerPage,
            }) as Promise<BeneficiariesResponse>,
    });

    const addBeneficiary = useMutation({
        mutationFn: (params: { idUser: number; relation: string }) =>
            groupsService.addBeneficiary(member.id, params),
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({
                queryKey: [FAMILY_BENEFICIARIES, member.id],
            });
            queryClient.invalidateQueries({ queryKey: [GROUP_MEMBERS] });
            setUser(null);
        },
        // 869ddddnj: el backend envía claves i18n envueltas en `[[...]]`
        // (p.ej. `[[api.userGroup.badRequest.fullOfBeneficiaries]]`).
        // `getErrorMessage` pasa el texto por `translatePlaceholders` y, si la
        // clave existe en `src/locales/{es,en}.json`, lo convierte en texto
        // legible para el toast.
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    const deleteBeneficiary = useMutation({
        mutationFn: (beneficiaryId: number) =>
            groupsService.deleteBeneficiary(beneficiaryId),
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({
                queryKey: [FAMILY_BENEFICIARIES, member.id],
            });
            queryClient.invalidateQueries({ queryKey: [GROUP_MEMBERS] });
        },
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    function addUserToBeneficiariesList() {
        if (user) {
            addBeneficiary.mutate({ idUser: user.id, relation });
        }
    }

    // -----| Render |-----
    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 7 }}>
                    <SttSelectUser
                        controlDisabled={false}
                        name="user"
                        caption={t('Name')}
                        user={user || undefined}
                        onSelect={(usuario) => {
                            if (usuario && 'user' in usuario) {
                                // It's a Mship, extract the user
                                setUser(usuario.user as User);
                            } else {
                                // It's a User or null
                                setUser(usuario as User | null);
                            }
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 3 }}>
                    <SttFormSelect
                        name="Relacion"
                        caption={t('relation')}
                        defVal={relation}
                        options={[
                            {
                                caption: t('offspring'),
                                value: 'OFFSPRING',
                            },
                            {
                                caption: t('partner'),
                                value: 'PARTNER',
                            },
                            {
                                caption: t('groups.beneficiaries.other'),
                                value: 'OTHER',
                            },
                        ]}
                        onChange={(value: {
                            name: string | undefined;
                            value: unknown;
                        }) => setRelation(value.value as string)}
                    />
                </Grid>
                <Grid size={{ xs: 2 }}>
                    <SttButton
                        variant="contained"
                        color="primary"
                        caption={t('Add')}
                        onClick={() => addUserToBeneficiariesList()}
                    />
                </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <SttTable
                    data={{
                        rows: beneficiariesQuery.data?.rows || [],
                        count: beneficiariesQuery.data?.count || 0,
                    }}
                    autoload={false}
                    perPage={20}
                    columns={[
                        {
                            title: t('User'),
                            name: 'user',
                            type: 'user',
                            value: (row: Beneficiary) => row.name,
                        },
                        {
                            title: t('Created'),
                            name: 'created',
                            // El backend guarda los datetime en hora de España: DateBuilder los pasa a la zona del centro y date-fns los formatea en el idioma del usuario.
                            value: (row: Beneficiary) =>
                                row.created
                                    ? safeFormatDate(
                                          new DateBuilder(
                                              row.created,
                                              cxt?.sc?.timezone?.name,
                                          ).ymdhms(),
                                          'd MMM yyyy HH:mm',
                                          { locale: getDateLocale() },
                                      )
                                    : '',
                        },
                        {
                            title: t('Relation'),
                            name: 'relation',
                            type: 'text',
                            value: (row: Beneficiary) =>
                                relationLabels[
                                    row.holderRelation?.toUpperCase() ?? ''
                                ] ??
                                row.holderRelation ??
                                '',
                        },
                        {
                            title: '',
                            value: (row: Beneficiary) => (
                                <DeleteIcon
                                    onClick={() => {
                                        deleteBeneficiary.mutate(row.id);
                                    }}
                                />
                            ),
                        },
                    ]}
                    onFetch={({
                        page: newPage,
                        rows,
                    }: {
                        page: number;
                        rows?: number;
                    }) => {
                        setPage(newPage);
                        if (rows) setRowsPerPage(rows);
                    }}
                />
            </Grid>
        </>
    );
}
